<template>
    <Teleport to="body">
        <div class="fixed inset-0 z-[9999] flex items-start justify-center pt-[15vh]">
            <div
                class="fixed inset-0 bg-slate-900/40 transition-opacity duration-150 ease-out"
                :class="open ? 'opacity-100' : 'opacity-0'"
                aria-hidden="true"
                @mousedown="emit('close')"
            ></div>

            <div
                class="relative w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden transition-all duration-150 ease-out"
                :class="open ? 'scale-100 opacity-100' : 'scale-95 opacity-0'"
                role="dialog"
                aria-modal="true"
                aria-label="Command palette"
            >
                <div class="flex items-center gap-3 border-b border-slate-200 px-4">
                    <font-awesome-icon class="text-slate-400 text-sm shrink-0" :icon="faMagnifyingGlass" />
                    <input
                        ref="searchInputRef"
                        v-model="query"
                        type="text"
                        role="combobox"
                        aria-expanded="true"
                        aria-controls="command-palette-listbox"
                        aria-autocomplete="list"
                        :aria-activedescendant="
                            visualItems.length > 0 ? 'command-palette-option-' + activeIndex : undefined
                        "
                        class="w-full py-4 text-sm outline-none bg-transparent placeholder-slate-400"
                        placeholder="Search pages..."
                        @keydown.escape.prevent="emit('close')"
                        @keydown.arrow-down.prevent="moveDown"
                        @keydown.arrow-up.prevent="moveUp"
                        @keydown.enter.prevent="selectCurrent"
                    />
                </div>

                <div
                    id="command-palette-listbox"
                    class="max-h-80 overflow-y-auto overscroll-contain p-1 scroll-thin"
                    role="listbox"
                >
                    <div v-if="visualItems.length === 0" class="px-4 py-8 text-center text-sm text-slate-400">
                        No results found.
                    </div>

                    <template v-for="group in groupedItems" :key="group.section ?? '_ungrouped'">
                        <div
                            v-if="group.section"
                            class="font-bold text-[10px] text-slate-400 uppercase tracking-wide px-4 pt-3 pb-1"
                        >
                            {{ group.section }}
                        </div>
                        <hr v-else class="my-2 border-slate-200" />
                        <a
                            v-for="item in group.items"
                            :id="'command-palette-option-' + item.flatIndex"
                            :key="item.href + item.label"
                            :ref="(el) => setItemRef(el, item.flatIndex)"
                            :href="item.href || undefined"
                            :target="item.target || undefined"
                            :rel="item.target ? 'noopener noreferrer' : undefined"
                            role="option"
                            :aria-selected="item.flatIndex === activeIndex"
                            class="flex items-center gap-3 px-4 py-2.5 rounded text-sm no-underline cursor-pointer"
                            :class="
                                item.flatIndex === activeIndex
                                    ? 'bg-sky-50 text-slate-900'
                                    : 'hover:bg-slate-50 text-slate-500'
                            "
                            @click="onItemClick($event, item)"
                            @mouseenter="activeIndex = item.flatIndex"
                        >
                            <font-awesome-icon
                                class="text-xs! shrink-0"
                                :class="'text-slate-400'"
                                :icon="item.icon ?? faArrowRight"
                            />
                            <div class="flex flex-col min-w-0">
                                <span class="truncate">{{ item.label }}</span>
                            </div>
                        </a>
                    </template>
                </div>

                <div class="flex items-center gap-4 border-t border-slate-200 px-4 py-2 text-xs text-slate-400">
                    <span class="flex items-center gap-1">
                        <kbd class="rounded border h-5 flex items-center border-slate-200 px-1 font-mono text-sm"
                            >&#x2191;</kbd
                        >
                        <kbd class="rounded border h-5 flex items-center border-slate-200 px-1 font-mono text-sm"
                            >&#x2193;</kbd
                        >
                        navigate
                    </span>
                    <span class="flex items-center gap-1">
                        <kbd class="rounded border h-5 flex items-center border-slate-200 px-1 font-mono text-sm"
                            >&#x23CE;</kbd
                        >
                        select
                    </span>
                    <span class="flex items-center gap-1">
                        <kbd class="rounded border h-5 flex items-center border-slate-200 px-1 font-mono text-xs"
                            >esc</kbd
                        >
                        close
                    </span>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script lang="ts" setup>
import { ref, computed, watch, nextTick, onMounted } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faArrowRight, faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons';
import { useFuse } from '@vueuse/integrations/useFuse';
import { navbarRoutes } from '~/components/navbar/navbar-routes';
import { settingsLinks } from '~/composables/settingsLinks';
import { paletteCommands } from '~/components/command-palette/paletteCommands';
import type { CommandPaletteItem } from '~/components/command-palette/paletteCommands';

const emit = defineEmits<{ close: [] }>();

const open = ref(false);
const query = ref('');
const activeIndex = ref(0);
const searchInputRef = ref<HTMLInputElement>();
const itemRefs = ref<(HTMLElement | null)[]>([]);

const sparkSettings = {
    isServiceRepresentative: Spark.isServiceRepresentative,
    ownsCurrentTeam: Spark.ownsTeam,
    isAdminRoleOnCurrentTeam: Spark.roleOnCurrentTeam === 'admin',
};

const navRoutes = navbarRoutes(sparkSettings);
const settingsLinksRef = settingsLinks();

const allItems = computed<CommandPaletteItem[]>(() => {
    const items: CommandPaletteItem[] = [];

    for (const route of navRoutes) {
        if ('items' in route && route.items) {
            for (const item of route.items) {
                if ('href' in item || 'onClick' in item) {
                    items.push({
                        label: item.name,
                        href: ('href' in item ? item.href : '') ?? '',
                        icon: route.icon ?? null,
                        section: route.name,
                        target: ('target' in item ? item.target : null) ?? null,
                        onClick: 'onClick' in item ? item.onClick : undefined,
                    });
                }
            }
        } else if ('href' in route && route.href) {
            items.push({
                label: route.name,
                href: String(route.href),
                icon: route.icon ?? null,
                section: route.name,
                target: ('target' in route ? route.target : null) ?? null,
            });
        }
    }

    for (const section of settingsLinksRef.value) {
        for (const link of section.links) {
            items.push({
                label: link.label,
                href: link.href,
                icon: link.icon ?? null,
                section: `Settings > ${section.label}`,
                target: null,
            });
        }
    }

    items.push(...paletteCommands());

    return items;
});

const { results: fuseResults } = useFuse(query, allItems, {
    fuseOptions: {
        keys: ['label', 'section', 'href'],
        threshold: 0.4,
    },
    matchAllWhenSearchEmpty: true,
});

type GroupedItem = CommandPaletteItem & { flatIndex: number };

const groupedItems = computed<{ section?: string; items: GroupedItem[] }[]>(() => {
    const groups: { section?: string; items: (CommandPaletteItem & { flatIndex: number })[] }[] = [];

    for (const { item } of fuseResults.value) {
        let group = groups.find((g) => g.section === item.section);
        if (!group) {
            group = { section: item.section, items: [] };
            groups.push(group);
        }
        group.items.push({ ...item, flatIndex: 0 });
    }

    let flatIndex = 0;
    for (const group of groups) {
        for (const item of group.items) {
            item.flatIndex = flatIndex++;
        }
    }

    return groups;
});

const visualItems = computed<GroupedItem[]>(() => {
    return groupedItems.value.flatMap((group) => group.items);
});

const setItemRef = (el: unknown, index: number) => {
    itemRefs.value[index] = el as HTMLElement | null;
};

const navigate = (item: CommandPaletteItem) => {
    emit('close');
    if (item.onClick) {
        item.onClick();
        return;
    }
    if (item.target === '_blank') {
        window.open(item.href, '_blank', 'noopener,noreferrer');
    } else {
        window.location.assign(item.href);
    }
};

const onItemClick = (event: MouseEvent, item: CommandPaletteItem) => {
    if (item.onClick) {
        event.preventDefault();
        navigate(item);
        return;
    }
    if (event.metaKey || event.ctrlKey || item.target === '_blank') {
        emit('close');
        return;
    }
    event.preventDefault();
    navigate(item);
};

const scrollActiveIntoView = () => {
    nextTick(() => {
        itemRefs.value[activeIndex.value]?.scrollIntoView({ block: 'nearest' });
    });
};

const moveDown = () => {
    if (activeIndex.value < visualItems.value.length - 1) {
        activeIndex.value++;
        scrollActiveIntoView();
    }
};

const moveUp = () => {
    if (activeIndex.value > 0) {
        activeIndex.value--;
        scrollActiveIntoView();
    }
};

const selectCurrent = () => {
    const item = visualItems.value[activeIndex.value];
    if (item) {
        navigate(item);
    }
};

watch(query, () => {
    activeIndex.value = 0;
});

onMounted(() => {
    nextTick(() => {
        searchInputRef.value?.focus();
        open.value = true;
    });
});
</script>
